import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSaveBreakdownVersion } from '@/hooks/useBreakdown';
import { Loader2 } from 'lucide-react';

interface SaveVersionDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVersionSaved?: (version: { id: string; name: string }) => void;
}

export function SaveVersionDialog({
  jobId,
  open,
  onOpenChange,
  onVersionSaved,
}: SaveVersionDialogProps) {
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const saveVersion = useSaveBreakdownVersion(jobId);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Snapshot name is required.');
      return;
    }
    setNameError(null);

    saveVersion.mutate(
      { name: trimmedName, tag: tag.trim() || null },
      {
        onSuccess: (data) => {
          setName('');
          setTag('');
          onOpenChange(false);
          onVersionSaved?.({ id: data.id, name: data.name });
        },
        onError: (err: Error) => {
          if (err.message?.toLowerCase().includes('already exists')) {
            setNameError(err.message);
          }
        },
      }
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setName('');
      setTag('');
      setNameError(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Snapshot</DialogTitle>
          <DialogDescription>
            Save a snapshot of the current breakdown. You can restore this snapshot later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="snapshot-name" className="text-sm font-medium text-gray-700">
              Snapshot Name
            </label>
            <Input
              id="snapshot-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              placeholder="e.g., Final estimate, Before change order"
              autoFocus
            />
            {nameError && (
              <p className="text-xs text-red-600">{nameError}</p>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="snapshot-tag" className="text-sm font-medium text-gray-700">
              Tag <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <Input
              id="snapshot-tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="e.g., Estimate, Final, Revised"
              maxLength={60}
            />
            <p className="text-xs text-gray-500">
              Short label to describe the purpose of this snapshot.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={saveVersion.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saveVersion.isPending}>
            {saveVersion.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Snapshot'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
